import React, { useMemo, useCallback } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { 
  formatYAxisValue, 
  formatHorizontalBarLabel, 
  getHorizontalBarChartConfig,
  formatCurrency 
} from './formatters';

/**
 * Interface base para dados de evolução mensal
 * Princípio ISP: Interface específica para evolução mensal
 */
export interface BaseEvolucaoMensal {
  periodo: string;
  valor: number;
  documentos: number;
  parceiros: number;
  produtos: number;
}

/**
 * Interface base para dados de parceiros (fornecedores/clientes)
 * Princípio ISP: Interface específica para parceiros
 */
export interface BaseParceiro {
  nome: string;
  valor_transacao: number;
  valor_total?: number;
  participacao_percentual: number;
  documentos: number;
  produtos_distintos: number;
}

/**
 * Interface base para dados de produtos
 * Princípio ISP: Interface específica para produtos
 */
export interface BaseProduto {
  cod_item: string;
  descricao_produto: string;
  valor_transacao: number;
  valor_total?: number;
  quantidade?: number;
}

/**
 * Interface base para dados de CFOP
 * Princípio ISP: Interface específica para CFOP
 */
export interface BaseCfop {
  cfop: string;
  descricao_cfop: string;
  valor_total: number;
  documentos: number;
  parceiros: number;
}

/**
 * Interface base para dados de NCM
 * Princípio ISP: Interface específica para NCM
 */
export interface BaseNcm {
  ncm: string;
  descricao_ncm: string;
  valor_total: number;
  quantidade_total: number;
  produtos_diferentes: number;
  parceiros_distintos: number;
}

/**
 * Interface base para informações da seção
 * Princípio ISP: Interface específica para informações da seção
 */
export interface BaseSecaoInfo {
  nome: string;
  total_transacao: number;
  participacao_percentual: number;
  total_documentos: number;
  parceiros_distintos: number;
  produtos_distintos: number;
  quantidade_total?: number;
  ticket_medio: number;
  credito_icms?: number;
}

/**
 * Interface base para dados de drill down de seção
 * Princípio ISP: Interface específica para dados de drill down
 */
export interface BaseSecaoDrillDownData {
  secao: BaseSecaoInfo;
  evolucao_mensal: BaseEvolucaoMensal[];
  top_parceiros: BaseParceiro[];
  principais_produtos: BaseProduto[];
  cfops_utilizados: BaseCfop[];
  analise_por_ncm?: BaseNcm[];
}

/**
 * Configuração para customização do drill down
 * Princípio OCP: Extensível sem modificar código existente
 */
export interface SecaoDrillDownConfig {
  transactionType: 'compras' | 'vendas';
  labels: {
    title: string;
    totalTransaction: string;
    creditIcms?: string;
    partners: string;
    products: string;
    documents: string;
    participation: string;
    ticketMedio: string;
    evolutionTitle: string;
    topPartnersTitle: string;
    topProductsTitle: string;
    cfopsTitle: string;
    ncmTitle: string;
  };
  colors: {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    info: string;
  };
  valueKeys: {
    transactionValue: keyof BaseSecaoInfo;
    partnerValue: keyof BaseParceiro;
    productValue: keyof BaseProduto;
    evolutionValue: keyof BaseEvolucaoMensal;
  };
}

/**
 * Props para o componente base
 * Princípio ISP: Interface específica para props do componente
 */
export interface BaseSecaoDrillDownContentProps {
  data: BaseSecaoDrillDownData;
  config: SecaoDrillDownConfig;
}

/**
 * Componente base para drill down de seções
 * Princípio SRP: Responsável apenas pela renderização de drill down de seções
 * Princípio OCP: Extensível via configuração sem modificar código
 * Princípio DIP: Depende de abstrações (interfaces) não de implementações concretas
 */
const BaseSecaoDrillDownContent: React.FC<BaseSecaoDrillDownContentProps> = ({ 
  data, 
  config 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Memoizar funções de formatação para melhor performance
  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value);
  }, []);

  const formatNumber = useCallback((value: number) => {
    return new Intl.NumberFormat("pt-BR").format(value);
  }, []);

  const formatPercent = useCallback((value: number) => {
    return `${value.toFixed(2)}%`;
  }, []);

  // Memoizar dados dos gráficos para evitar re-processamento
  const evolucaoData = useMemo(() => {
    if (!data?.evolucao_mensal || data.evolucao_mensal.length === 0) {
      return [];
    }
    
    // Garantir que todos os dados estão válidos
    const dadosValidos = data.evolucao_mensal.filter(item => 
      item.periodo && 
      item.valor !== undefined && 
      item.valor !== null && 
      !isNaN(item.valor)
    );
    
    if (dadosValidos.length === 0) {
      return [];
    }
    
    const baseData = {
      id: config.transactionType === 'compras' ? 'Compras' : 'Vendas',
      data: dadosValidos.map(item => ({
        x: item.periodo,
        y: Number(item.valor) || 0,
        documentos: Number(item.documentos) || 0,
        parceiros: Number(item.parceiros) || 0,
        produtos: Number(item.produtos) || 0
      }))
    };

    return [baseData];
  }, [data.evolucao_mensal, config.transactionType, data.secao.credito_icms]);

  // Verificar se há dados válidos para exibir
  const hasValidEvolutionData = evolucaoData.length > 0 && evolucaoData[0].data.length > 0;

  const parceirosData = useMemo(() => {
    const parceiros = data.top_parceiros || [];
    if (!Array.isArray(parceiros)) return [];
    
    // Ordenar por valor de transação em ordem crescente (menor para maior)
    return parceiros
      .slice(0, 10)
      .sort((a, b) => (a.valor_total || a.valor_transacao || 0) - (b.valor_total || b.valor_transacao || 0))
      .map(parceiro => ({
        parceiro: formatHorizontalBarLabel(parceiro.nome || '', 30),
        parceiroCompleto: parceiro.nome || '', // Nome completo para tooltip
        valor: (parceiro.valor_total || parceiro.valor_transacao) ?? 0,
        participacao: parceiro.participacao_percentual ?? 0,
        documentos: parceiro.documentos ?? 0,
        produtos: parceiro.produtos_distintos ?? 0
      }));
  }, [data.top_parceiros]);

  const produtosData = useMemo(() => {
    const produtos = data.principais_produtos || [];
    if (!Array.isArray(produtos)) return [];
    
    // Ordenar por valor de transação em ordem crescente (menor para maior)
    return produtos
      .slice(0, 10)
      .sort((a, b) => (a.valor_total || a.valor_transacao || 0) - (b.valor_total || b.valor_transacao || 0))
      .map(produto => ({
        produto: formatHorizontalBarLabel(produto.cod_item || '', 30),
        produtoCompleto: produto.cod_item || '', // Código completo para tooltip
        valor: (produto.valor_total || produto.valor_transacao) ?? 0,
        descricao: produto.descricao_produto ?? '',
        descricaoCompleta: produto.descricao_produto ?? '' // Descrição completa para tooltip
      }));
  }, [data.principais_produtos]);

  // Configurações responsivas para gráficos
  const chartMargins = useMemo(() => ({
    line: isMobile 
      ? { top: 30, right: 60, bottom: 100, left: 80 }
      : { top: 50, right: 110, bottom: 50, left: 60 },
    bar: isMobile
      ? { top: 20, right: 30, bottom: 60, left: 80 }
      : { top: 20, right: 30, bottom: 50, left: 150 }
  }), [isMobile]);

  return (
    <Grid container spacing={3}>
      {/* Header com informações principais */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography 
              variant="subtitle1"
              align="center"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              📋 {config.labels.title}: {data.secao?.nome || 'N/A'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: config.colors.success, borderRadius: 2 }}>
                  <Typography variant="h6" color="white">
                    {formatCurrency(data.secao?.total_transacao || 0)}
                  </Typography>
                  <Typography variant="body2" color="white">
                    {config.labels.totalTransaction}
                  </Typography>
                </Box>
              </Grid>
              {config.transactionType === 'compras' && data.secao.credito_icms !== undefined && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: config.colors.primary, borderRadius: 2 }}>
                    <Typography variant="h6" color="white">
                      {formatCurrency(data.secao?.credito_icms || 0)}
                    </Typography>
                    <Typography variant="body2" color="white">
                      {config.labels.creditIcms}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={config.transactionType === 'compras' ? 3 : 4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: config.colors.info, borderRadius: 2 }}>
                  <Typography variant="h6" color="white">
                    {formatNumber(data.secao?.parceiros_distintos || 0)}
                  </Typography>
                  <Typography variant="body2" color="white">
                    {config.labels.partners}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={config.transactionType === 'compras' ? 3 : 4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: config.colors.warning, borderRadius: 2 }}>
                  <Typography variant="h6" color="white">
                    {formatNumber(data.secao?.produtos_distintos || 0)}
                  </Typography>
                  <Typography variant="body2" color="white">
                    {config.labels.products}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{config.labels.participation}:</strong> {formatPercent(data.secao.participacao_percentual)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{config.labels.ticketMedio}:</strong> {formatCurrency(data.secao.ticket_medio)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{config.labels.documents}:</strong> {formatNumber(data.secao.total_documentos)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Gráfico de evolução */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography 
              variant="subtitle1"
              align="center"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              📈 {config.labels.evolutionTitle}
            </Typography>
            <Box sx={{ height: isMobile ? 300 : 400 }}>
              {hasValidEvolutionData ? (
                <ResponsiveLine
                  data={evolucaoData}
                  margin={chartMargins.line}
                  xScale={{ type: 'point' }}
                  yScale={{ 
                    type: 'linear', 
                    min: 'auto', 
                    max: 'auto',
                    stacked: false,
                    reverse: false
                  }}
                  curve="catmullRom"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: isMobile ? -45 : 0,
                    legend: 'Período',
                    legendOffset: isMobile ? 80 : 56,
                    legendPosition: 'middle'
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    legend: 'Valor (R$)',
                    legendOffset: -50,
                    legendPosition: 'middle',
                    format: formatYAxisValue
                  }}
                  pointSize={8}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={3}
                  pointBorderColor={{ from: 'serieColor' }}
                  colors={[config.colors.success, config.colors.primary]}
                  lineWidth={3}
                  useMesh={true}
                  crosshairType="cross"
                  enableSlices="x"
                  tooltip={({ point }) => {
                    const pointData = point.data as any;
                    return (
                      <Card sx={{ p: 2 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {point.serieId}: {formatCurrency(point.data.y as number)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {String(point.data.x)}
                        </Typography>
                        {pointData.documentos && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatNumber(pointData.documentos)} documentos
                          </Typography>
                        )}
                      </Card>
                    );
                  }}
                  animate={true}
                  motionConfig="gentle"
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      translateY: isMobile ? 90 : 70, // Mais baixo para evitar sobreposição
                      itemsSpacing: 50, // Espaçamento equilibrado entre itens
                      itemDirection: 'left-to-right',
                      itemWidth: 100, // Largura compacta (símbolo + texto próximos)
                      itemHeight: 22,
                      symbolSize: 12, // Símbolo padrão
                      symbolShape: 'circle'
                    }
                  ]}
                />
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">Nenhum dado de evolução disponível</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Parceiros */}
      <Grid item xs={12} md={6}>
        <Card sx={{ overflow: 'visible' }}>
          <CardContent sx={{ overflow: 'visible' }}>
            <Typography variant="h6" gutterBottom>
              {config.transactionType === 'compras' ? '🏢' : '👥'} {config.labels.topPartnersTitle}
            </Typography>
            <Box sx={{ 
              height: isMobile ? 300 : 400,
              overflow: 'visible', // Permite tooltip fora dos limites
              position: 'relative',
              zIndex: 1 // Garante que tooltips fiquem acima
            }}>
              {parceirosData.length > 0 ? (
                <ResponsiveBar
                  data={parceirosData}
                  keys={['valor']}
                  indexBy="parceiro"
                  enableLabel={true}
                  layout="horizontal"
                  margin={getHorizontalBarChartConfig(10, true).margin}
                  padding={0.3}
                  colors={[config.colors.primary]}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: `${config.labels.totalTransaction} (R$)`,
                    legendPosition: 'middle',
                    legendOffset: 40,
                    format: formatYAxisValue
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0
                  }}
                  labelTextColor="#fff"
                  valueFormat={formatCurrency}
                  tooltip={({ value, indexValue, data }) => (
                    <Card sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {indexValue}
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(value as number)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(data as any).participacao?.toFixed(1)}% do total
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(data as any).documentos} documentos • {(data as any).produtos} produtos
                      </Typography>
                    </Card>
                  )}
                />
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">Nenhum cliente encontrado para esta seção</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Produtos */}
      <Grid item xs={12} md={6}>
        <Card sx={{ overflow: 'visible' }}>
          <CardContent sx={{ overflow: 'visible' }}>
            <Typography variant="h6" gutterBottom>
              📦 {config.labels.topProductsTitle}
            </Typography>
            <Box sx={{ 
              height: isMobile ? 300 : 400,
              overflow: 'visible', // Permite tooltip fora dos limites
              position: 'relative',
              zIndex: 1 // Garante que tooltips fiquem acima
            }}>
              {produtosData.length > 0 ? (
                <ResponsiveBar
                  data={produtosData}
                  keys={['valor']}
                  indexBy="produto"
                  enableLabel={true}
                  layout="horizontal"
                  margin={getHorizontalBarChartConfig(10, true).margin}
                  padding={0.3}
                  colors={[config.colors.success]}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: `${config.labels.totalTransaction} (R$)`,
                    legendPosition: 'middle',
                    legendOffset: 40,
                    format: formatYAxisValue
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0
                  }}
                  labelTextColor="#fff"
                  valueFormat={formatCurrency}
                  tooltip={({ value, indexValue, data }) => (
                    <Card sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {indexValue}
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(value as number)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(data as any).descricao}
                      </Typography>
                    </Card>
                  )}
                />
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">Nenhum produto encontrado para esta seção</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Tabela de CFOPs */}
      {data.cfops_utilizados && data.cfops_utilizados.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 {config.labels.cfopsTitle}
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CFOP</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">Documentos</TableCell>
                      <TableCell align="right">{config.labels.partners}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.cfops_utilizados && Array.isArray(data.cfops_utilizados) ? (
                      data.cfops_utilizados.slice(0, 10).map((cfop, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip label={cfop?.cfop || 'N/A'} color="primary" size="small" />
                          </TableCell>
                          <TableCell>{cfop?.descricao_cfop || 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main" fontWeight={500}>
                              {formatCurrency(cfop?.valor_total || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatNumber(cfop?.documentos || 0)}</TableCell>
                          <TableCell align="right">{formatNumber(cfop?.parceiros || 0)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Nenhum CFOP encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Tabela de Análise por NCM - apenas para compras */}
      {config.transactionType === 'compras' && data.analise_por_ncm && data.analise_por_ncm.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔢 {config.labels.ncmTitle}
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NCM</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Produtos</TableCell>
                      <TableCell align="right">{config.labels.partners}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.analise_por_ncm && Array.isArray(data.analise_por_ncm) ? (
                      data.analise_por_ncm.slice(0, 10).map((ncm, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip label={ncm?.ncm || 'N/A'} color="secondary" size="small" />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>{ncm?.descricao_ncm || 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main" fontWeight={500}>
                              {formatCurrency(ncm?.valor_total || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatNumber(ncm?.quantidade_total || 0)}</TableCell>
                          <TableCell align="right">{formatNumber(ncm?.produtos_diferentes || 0)}</TableCell>
                          <TableCell align="right">{formatNumber(ncm?.parceiros_distintos || 0)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nenhum NCM encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default BaseSecaoDrillDownContent; 